/**
 * The row of small cards at the top of the Overview that says which parts of
 * Wend this workspace has — slice 65.
 *
 * ## WHY THE OVERVIEW NEEDED IT
 *
 * Until slice 65 the product had one built pillar a person could send with,
 * and the Overview was its dashboard: nothing on the page had to say which
 * part of Wend it belonged to, because there was only one. Slice 65 split the
 * old Reach into **Reach** (paid ads on the workspace's own Meta ad account)
 * and **Connect** (email and WhatsApp), beside **Sense**. The dashboard below
 * this strip is Connect's — every figure on it is a message Wend handed to
 * SES or Meta's Cloud API — so a workspace that runs ads, or reads Today, had
 * nothing on its first screen saying those exist or where they start.
 *
 * This is the smallest thing that answers it: one card per pillar this
 * workspace **has**, each a glyph, the name and one line on what it is for,
 * linking to where that pillar starts. Until slice 70 that was the whole of
 * it — no figures, no counts, no "3 findings today" — because a figure here
 * would be a second copy of one the pillar's own screen draws with its
 * caveats. Slice 70 changed that on the owner's word; see the next section.
 *
 * ## SLICE 70: EACH CARD SAYS WHAT ITS PILLAR IS DOING
 *
 * "I should be able to see all 4 pillars working together." With Flow built,
 * the strip is the one place a reader sees the loop at a glance, and a card
 * that only names a pillar cannot show it working. So each card gains ONE
 * line under its purpose — Reach's leads this week or "connect your ad
 * account", Connect's scheduled campaigns, Sense's findings waiting, Flow's
 * live flows and the people walking them — read from `GET /overview/pillars`
 * (`contracts/pillar-status.ts`), which computes them on read from real rows.
 *
 * The line is the API's `headline`, printed as given: one sentence written
 * where the figures are counted, so the strip cannot pair a number with the
 * wrong noun or turn "no ad account" into "0 leads". It is not a second copy
 * of a pillar screen's figure in the sense the old rule feared — no rate, no
 * caveat-bearing measurement, only counts of rows Wend owns, and the API's
 * contract keeps `null` for "not measured" (no ad account, never a lead).
 *
 * **Absent until it arrives, and absent if it fails.** The read is fetched by
 * the strip itself, after mount, through a dynamic import (see
 * `pillar-status-read.ts`). Until it answers, and if it never does, a card is
 * exactly the slice 65 card — name, purpose, link — and never a "0" or a
 * spinner standing in for one. The line is `aria-live="polite"` because it
 * arrives after the card is on screen.
 *
 * ## WHICH PILLARS, AND WHY NEVER ONE THAT IS NOT ON THIS PLAN
 *
 * The first cut drew every built pillar, with "Available" or "Not on this
 * plan" on each. Review caught what that did to the workspaces the strip was
 * for: a Connect-only bank opened its Overview on two cards of software it
 * does not have — one of them Sense, which the public site says is not
 * available today — while its sidebar deliberately left both out, because an
 * unentitled Reach or Sense "reads as an upsell" (slice 59's rule). Two
 * screens answering "which parts do I have?" differently is the problem the
 * shared pillar vocabulary exists to stop.
 *
 * So the strip asks `pillarIsShown` in `lib/pillars.tsx` — the sidebar's own
 * rule — and then draws only the pillars that are **available**. The one
 * pillar that rule lets through unentitled is Connect, and on this page its
 * absence is already said, once, by `PillarSection` in the dashboard's place;
 * a strip card saying "Not on this plan" above an `h2` saying it again was the
 * second copy. With every card on the plan, a status badge would say
 * "Available" on all of them and so say nothing, and there is none: a card
 * being a link is the statement.
 *
 * **One card is no strip.** A workspace with Connect and nothing else would
 * get a lone "Connect" card linking to Campaigns above Connect's own
 * dashboard — a table of contents with one entry, for the page it heads. The
 * strip draws nothing then, and that workspace's Overview is the dashboard it
 * always was. A lone Reach or Sense card *is* drawn: on a plan without
 * Connect it is the only word on the page about the pillar the workspace does
 * have.
 *
 * ## WHAT IT READS, AND THE ONE FALLBACK
 *
 * The states come from `GET /overview`'s `pillars` rows — the same verdict
 * `overviewPillar` gives the sidebar. {@link pillarStateOf} is also what
 * `HomePage` gates the dashboard and its header actions on, so the strip and
 * the dashboard under it are decided by one function.
 *
 * A missing **Connect** row reads as available. During a deploy the web app
 * can be one release ahead of the API, and an API from before slice 65 sends
 * no `connect` row at all; reading that absence as "not on this plan" would
 * blank the dashboard of every workspace that sends for as long as the two
 * were out of step. Entitlement is advisory in this product (no route checks
 * it), so for the pillar whose content *is* this page the cheaper error is to
 * show a screen, not hide one. A missing **Reach or Sense** row is no such
 * case — every API that knows those words sends their rows — so it reads as
 * not on this plan: the strip would otherwise link into a pillar the sidebar,
 * built from the session, does not offer. Flow's reads as not built.
 *
 * ## WHAT IT DELIBERATELY DOES NOT DO
 *
 * - **No unbuilt pillar is drawn**, not as a greyed card and not as "coming
 *   soon". Slice 26 took the unbuilt pillars off this page on the owner's
 *   word. Until slice 70 that meant Flow; Flow is built now, so it is a fourth
 *   card on a plan that has it, from `BUILT_PILLARS` like the others.
 * - **Nothing offers to buy or upgrade.** The one place a plan changes is the
 *   staff console.
 * - **No heading per card.** The names are link text, not `h2`s: headings of
 *   one word each, above a dashboard whose cards carry their own, would make
 *   the page outline lead with entries that hold nothing. The list is
 *   labelled once, for a screen reader, in words a reader would use ("Parts of
 *   Wend" — "pillar" is this codebase's word, not theirs), and each link is
 *   described by its purpose line.
 *
 * ## THE KICKER NAMES THE SCREEN THE CARD OPENS (slice 66)
 *
 * The Blueprint's pillar node is a kicker over a name, and its kicker was a
 * status — "Available today". This strip cannot say that: every card on it is
 * on the plan, so a status would say "Available" three times and so say
 * nothing, which is the badge slice 65's review took off. The kicker here is
 * instead the one fact the card did not already carry in words: **which
 * screen the link opens** — Ads, Campaigns, Today, Flows. A reader choosing a part of
 * Wend learns where they will land before they press, and the purpose line
 * stays the description of the link.
 *
 * It is one word per card on purpose. `verify-dashboard` holds the strip under
 * a word ceiling at its longest (raised in slice 70 for the fourth card and
 * the live lines, by the measurement plus a small headroom), and a second
 * word in a kicker ("Opens Campaigns") is still the wrong place to spend it:
 * the strip is a table of contents with a pulse, not a card family.
 * The words are written here, beside the links they name, rather than added
 * to `PILLAR_IDENTITY`, which every pillar surface reads and none other needs.
 *
 * The glyph tile is the Blueprint's emphasised-source tile: the horizontal
 * brand sweep with a white glyph. It was `text-brand-b` on a brand tint, a
 * pairing the suite's contrast table rules out for text (4.47:1 on a dark
 * surface, lower on the tint). It is the sweep and not the primary gradient
 * because that class means "the primary action" to every suite that counts
 * it, and a decorative glyph is not one.
 *
 * ## THE CHROME IS THE OVERVIEW'S
 *
 * 18px of inset, like every card in this folder (HomePage's header, THE
 * OVERVIEW IS ONE CARD FAMILY, and `verify-dashboard` counts it). The cards
 * are shorter than the rest because they say less, not because they are a
 * different family.
 */
import {
  PILLAR_IS_BUILT,
  type Feature,
  type OverviewPillar,
  type PillarState,
  type PillarStatusResponse,
} from '@wend/shared';
import { Card, CardContent, Kicker, cn, focusRing } from '@wend/ui';
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { BUILT_PILLARS, PILLAR_IDENTITY, pillarIsShown, type BuiltPillar } from '../../lib/pillars';

/**
 * A pillar's state as `GET /overview` reported it, or — when the row is
 * missing — the fallback the header argues: Connect available, Reach and
 * Sense not on this plan. Flow, built since slice 69, reads like Reach and
 * Sense — a missing row is not on this plan. (Until then it read as not built.)
 */
export function pillarStateOf(pillars: readonly OverviewPillar[], feature: Feature): PillarState {
  const row = pillars.find((pillar) => pillar.feature === feature);
  if (row !== undefined) return row.state;
  if (!PILLAR_IS_BUILT[feature]) return 'not_built';
  return feature === 'connect' ? 'available' : 'not_entitled';
}

/** The pillars the strip draws: shown by the sidebar's rule, and on this plan. */
export function stripPillars(pillars: readonly OverviewPillar[]): readonly BuiltPillar[] {
  return BUILT_PILLARS.filter((feature) => {
    const state = pillarStateOf(pillars, feature);
    return pillarIsShown(feature, state) && state === 'available';
  });
}

/**
 * The live line for one pillar, or `null` — no read yet, a failed read, or a
 * pillar the read says is not available. Never a default.
 */
export function pillarHeadline(status: PillarStatusResponse | null, feature: Feature): string | null {
  if (status === null) return null;
  const card = status.cards.find((one) => one.pillar.feature === feature);
  if (card === undefined || card.live === null || card.live.feature !== feature) return null;
  return card.live.headline;
}

/**
 * Fetches `GET /overview/pillars` once the strip is on screen. The reader is
 * imported dynamically so this module stays mountable outside Vite (see
 * `pillar-status-read.ts`). A failure leaves `null`: the cards are drawn
 * without their line, never with a guessed one.
 */
function usePillarStatus(enabled: boolean): PillarStatusResponse | null {
  const [status, setStatus] = useState<PillarStatusResponse | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    import('./pillar-status-read')
      .then(({ fetchPillarStatus }) => fetchPillarStatus(controller.signal))
      .then(
        (read) => {
          if (!controller.signal.aborted) setStatus(read);
        },
        () => {
          /* Absent, by design: the card still names its pillar and links to it. */
        },
      );
    return () => controller.abort();
  }, [enabled]);
  return status;
}

export interface PillarStripProps {
  readonly pillars: readonly OverviewPillar[];
  /**
   * The live read, when the caller already has it — a suite rendering a
   * state. Omitted, the strip fetches it itself after mount; `null` draws the
   * cards without their line and fetches nothing.
   */
  readonly live?: PillarStatusResponse | null;
}

export function PillarStrip({ pillars, live }: PillarStripProps) {
  const shown = stripPillars(pillars);
  const drawn = !(shown.length === 0 || (shown.length === 1 && shown[0] === 'connect'));
  const fetched = usePillarStatus(live === undefined && drawn);
  const status = live === undefined ? fetched : live;

  /* None, or Connect alone over Connect's own dashboard: no strip. See the header. */
  if (!drawn) return null;

  return (
    <ul
      aria-label="Parts of Wend"
      className={cn('grid grid-cols-1 gap-3', shown.length >= 4 ? 'sm:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3')}
      data-role="pillar-strip"
    >
      {shown.map((feature) => (
        <li key={feature} className="min-w-0">
          <PillarTile feature={feature} headline={pillarHeadline(status, feature)} />
        </li>
      ))}
    </ul>
  );
}

/** The screen each pillar's link opens, as the sidebar names it. See the header. */
const OPENS: Readonly<Record<BuiltPillar, string>> = {
  reach: 'Ads',
  connect: 'Campaigns',
  sense: 'Today',
  flow: 'Flows',
};

/**
 * One pillar. The name is the link, stretched over the whole card with an
 * `after:` layer, so the card is one target without a block-level `<a>`
 * wrapping the purpose line — and the link's accessible name is the pillar's,
 * with the purpose line as its description rather than a run-on of both.
 */
function PillarTile({ feature, headline }: { feature: BuiltPillar; headline: string | null }) {
  const identity = PILLAR_IDENTITY[feature];
  const purposeId = useId();

  return (
    <Card variant="interactive" className="relative h-full" data-pillar={feature}>
      <CardContent className="flex h-full items-start gap-3 px-[18px] py-3.5">
        <span
          aria-hidden="true"
          className="grid size-8 shrink-0 place-items-center rounded-sm border border-brand-b/30 bg-brand-sweep text-white [&_svg]:size-4"
        >
          {identity.icon}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Kicker as="span" tone="brand">
            {OPENS[feature]}
          </Kicker>
          <Link
            to={identity.landing}
            aria-describedby={purposeId}
            className={cn(
              'self-start text-[0.95rem] font-semibold tracking-[-0.015em] text-text',
              'rounded-sm after:absolute after:inset-0 after:rounded-lg',
              focusRing,
            )}
          >
            {identity.label}
          </Link>
          <p id={purposeId} className="text-[0.82rem] leading-snug text-muted">
            {identity.purpose}
          </p>
          {/* Always mounted, so the line is announced when it arrives; empty until then. */}
          <p aria-live="polite" className="text-[0.82rem] font-medium leading-snug text-text" data-role="pillar-live">
            {headline}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
